var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (_) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import { CorrelationVector } from './CorrelationVector';
import { logNotification, LogLevel, Category } from './OTelNotifications';
// Currently we only send one activity at a time
var ACTIVITY_COUNT = 1;
// Currently we do not aggregate activities in OTelJS
var ACTIVITY_AGGMODE = 0;
var getCurrentMicroseconds = function () {
    return Date.now() * 1000;
};
if (typeof window === 'object' && typeof window.performance === 'object' && 'now' in window.performance) {
    getCurrentMicroseconds = function () {
        return Math.floor(window.performance.now()) * 1000;
    };
}
/**
 * An activity scope, which automatically records duration and correlation vectors.
 * Activity scopes must have their result set with setResult or setSuccess.
 * The can be auto-ending when created with executeSync/executeAsync,
 * or their scope can be manually managed with createNew and endNow.
 * Child activities can be created with corresponding functions.
 */
var ActivityScope = /** @class */ (function () {
    function ActivityScope(telemetryLogger, activityName, parent) {
        this._optionalEventFlags = {};
        this._ended = false;
        this._telemetryLogger = telemetryLogger;
        this._activityName = activityName;
        if (parent) {
            this._cv = CorrelationVector.getNextChild(parent._cv);
        }
        else {
            this._cv = CorrelationVector.getNext();
        }
        this._dataFields = [];
        this._success = undefined;
        this._startTime = getCurrentMicroseconds();
    }
    /**
     * Creates a new Activity Scope that will be managed by the caller
     * One of setSuccess or setResult must be called on the scope, followed by endNow.
     * @param telemetryLogger - The telemetry logger that the activity belongs to
     * @param activityName - The name of the activity
     */
    ActivityScope.createNew = function (telemetryLogger, activityName) {
        return new ActivityScope(telemetryLogger, activityName);
    };
    /**
     * Creates an activity parented to the current activity. Does not start the activity.
     * @param activityName - The name of the activity
     */
    ActivityScope.prototype.createChildActivity = function (activityName) {
        var childActivity = new ActivityScope(this._telemetryLogger, activityName, this);
        return childActivity;
    };
    /**
     * Sets optional event flags.
     * @param eventFlags - Event flags to set
     */
    ActivityScope.prototype.setEventFlags = function (eventFlags) {
        this._optionalEventFlags = eventFlags;
    };
    /**
     * Adds a data field to the activity
     * @param dataField - A data field to add
     */
    ActivityScope.prototype.addDataField = function (dataField) {
        this._dataFields.push(dataField);
    };
    /**
     * Adds data fields to the activity
     * @param dataFields - Data fields to add
     */
    ActivityScope.prototype.addDataFields = function (dataFields) {
        var _a;
        (_a = this._dataFields).push.apply(_a, dataFields);
    };
    /**
     * Sets whether the activity succeeded
     * @param success - Whether the activity succeeded
     */
    ActivityScope.prototype.setSuccess = function (success) {
        this._success = success;
    };
    /**
     * Sets the result of the activity
     * @param resultCode - The result code, e.g. 0 for success
     * @param resultType - Optional: The result type, e.g. "HRESULT"
     * @param resultTag - Optional: The result tag
     */
    // tslint:disable-next-line no-reserved-keywords
    ActivityScope.prototype.setResult = function (code, type, tag) {
        this._result = { code: code, type: type, tag: tag };
    };
    /**
     * Ends the activity; records the duration and sends a telemetry event.
     */
    ActivityScope.prototype.endNow = function () {
        if (this._ended) {
            logNotification(LogLevel.Error, Category.Core, function () { return 'Activity has already ended'; });
            return;
        }
        if (this._success === undefined && this._result === undefined) {
            // The Activity dashboard shows these activities as 'unresolved'
            // but developers generally should avoid leaving activities in this state.
            logNotification(LogLevel.Warning, Category.Core, function () { return 'Activity does not have success or result set'; });
        }
        var endTime = getCurrentMicroseconds();
        var duration = endTime - this._startTime;
        this._ended = true;
        var activity = {
            duration: duration,
            count: ACTIVITY_COUNT,
            aggMode: ACTIVITY_AGGMODE,
            cV: this._cv.getString(),
            success: this._success,
            result: this._result
        };
        return this._telemetryLogger.sendActivity(this._activityName, activity, this._dataFields, this._optionalEventFlags);
    };
    /**
     * Starts an activity to execute asynchronously
     * You must call setSuccess or setResult on the activity inside the activity body.
     * When the activity body resolves or rejects, the duration is recorded and the activity is sent.
     * The promise resolution is passed through to the caller.
     * @param activityBody - The body of the activity, which should return a promise.
     */
    ActivityScope.prototype.executeAsync = function (activityBody) {
        return __awaiter(this, void 0, void 0, function () {
            var _this = this;
            return __generator(this, function (_a) {
                return [2 /*return*/, activityBody(this)
                        .then(function (result) {
                        _this.endNow();
                        return result;
                    })
                        .catch(function (e) {
                        _this.endNow();
                        throw e;
                    })];
            });
        });
    };
    /**
     * Starts an activity to execute synchronously
     * You must call setSuccess or setResult on the activity inside the activity body.
     * When the function returns, the duration is recorded and the activity is sent. The return value is passed through to the caller.
     * @param activityBody - The body of the activity, which should return a promise.
     */
    ActivityScope.prototype.executeSync = function (activityBody) {
        try {
            var ret = activityBody(this);
            this.endNow();
            return ret;
        }
        catch (e) {
            this.endNow();
            throw e;
        }
    };
    /**
     * Starts an activity to execute asynchronously, parented to the current activity.
     * @param activityName - The name of the activity
     * @param activityBody - The body of the activity
     */
    ActivityScope.prototype.executeChildActivityAsync = function (activityName, activityBody) {
        return __awaiter(this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                return [2 /*return*/, this.createChildActivity(activityName).executeAsync(activityBody)];
            });
        });
    };
    /**
     * Starts an activity to execute synchronously, parented to the current activity.
     * @param activityName - The name of the activity
     * @param activityBody - The body of the activity
     */
    ActivityScope.prototype.executeChildActivitySync = function (activityName, activityBody) {
        return this.createChildActivity(activityName).executeSync(activityBody);
    };
    return ActivityScope;
}());
export { ActivityScope };
//# sourceMappingURL=Activity.js.map